import { useCallback, useState } from 'react';
import { A03, A03Output } from 'api/U002';
import { FetchError } from 'api/API';

type CreateTemporaryUserRequest = {
  phoneNumber: string;
  sessionId: string;
};

type CreateTemporaryUserDoRequest = (param: {
  request: CreateTemporaryUserRequest;
  onSuccess?: (response: A03Output) => void;
  onError?: (err: Error) => void;
  cancellation?: AbortSignal;
}) => Promise<void>;

export function usePhoneVerification(): [CreateTemporaryUserDoRequest, boolean, Error | null] {
  const [loading, setLoading] = useState<boolean>(false);
  const [err, setError] = useState<Error | null>(null);

  const phoneVerification = async (
    request: CreateTemporaryUserRequest,
    cancellation: AbortSignal
  ) => {
    const json = await A03(request, cancellation);
    return json;
  };

  const doRequest = useCallback<CreateTemporaryUserDoRequest>(
    async ({ request, cancellation, onSuccess, onError }) => {
      const controller = new AbortController();
      const cancelSignal = cancellation ?? controller.signal;
      setLoading(true);
      try {
        const result = await phoneVerification(request, cancelSignal);
        onSuccess?.(result);
      } catch (err) {
        console.error({ err });
        if (err instanceof FetchError) {
          const errorData = await err.response.json();
          const flooredError = new Error(errorData.message || `HTTP ERROR: ${err.message}`);
          onError?.(flooredError);
          setError(flooredError);
        } else {
          const flooredError = new Error(`unknown error. ${err}`);
          onError?.(flooredError);
          setError(flooredError);
        }
      } finally {
        setLoading(false);
      }
    },
    [phoneVerification, setLoading, setError]
  );

  return [doRequest, loading, err];
}
